"use client"

import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  AlertTriangle,
  Check,
  Globe,
  CreditCard,
  Clock,
  LayoutGrid,
  LinkIcon,
  Mail,
  Hash,
  ChevronDown,
  Loader2,
  AlertCircle,
  Lock,
} from "lucide-react"
import toast from "react-hot-toast"
import { SearchableSelect, InputCustom } from "../../components"
import creditAccountsService from "../../services/creditAccountsService"
import adAccountsService from "../../services/adAccountsService"
import { useCurrentUser } from "../../utils/context/UserProvider";

// ───────────────────────────────── Icons ─────────────────────────────────
const TikTokIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="35" viewBox="0 0 48 48">
    <path fill="#212121" fillRule="evenodd" d="M10.904,6h26.191C39.804,6,42,8.196,42,10.904v26.191 C42,39.804,39.804,42,37.096,42H10.904C8.196,42,6,39.804,6,37.096V10.904C6,8.196,8.196,6,10.904,6z" clipRule="evenodd"></path>
    <path fill="#ec407a" fillRule="evenodd" d="M29.208,20.607c1.576,1.126,3.507,1.788,5.592,1.788v-4.011 c-0.395,0-0.788-0.041-1.174-0.123v3.157c-2.085,0-4.015-0.663-5.592-1.788v8.184c0,4.094-3.321,7.413-7.417,7.413 c-1.528,0-2.949-0.462-4.129-1.254c1.347,1.376,3.225,2.23,5.303,2.23c4.096,0,7.417-3.319,7.417-7.413L29.208,20.607L29.208,20.607 z M30.657,16.561c-0.805-0.879-1.334-2.016-1.449-3.273v-0.516h-1.113C28.375,14.369,29.331,15.734,30.657,16.561L30.657,16.561z M19.079,30.832c-0.45-0.59-0.693-1.311-0.692-2.053c0-1.873,1.519-3.391,3.393-3.391c0.349,0,0.696,0.053,1.029,0.159v-4.1 c-0.389-0.053-0.781-0.076-1.174-0.068v3.191c-0.333-0.106-0.68-0.159-1.03-0.159c-1.874,0-3.393,1.518-3.393,3.391 C17.213,29.127,17.972,30.274,19.079,30.832z" clipRule="evenodd"></path>
    <path fill="#fff" fillRule="evenodd" d="M28.034,19.63c1.576,1.126,3.507,1.788,5.592,1.788v-3.157 c-1.164-0.248-2.194-0.856-2.969-1.701c-1.326-0.827-2.281-2.191-2.561-3.788h-2.923v16.018c-0.007,1.867-1.523,3.379-3.393,3.379 c-1.102,0-2.081-0.525-2.701-1.338c-1.107-0.558-1.866-1.705-1.866-3.029c0-1.873,1.519-3.391,3.393-3.391 c0.359,0,0.705,0.056,1.03,0.159V21.38c-4.024,0.083-7.26,3.369-7.26,7.411c0,2.018,0.806,3.847,2.114,5.183 c1.18,0.792,2.601,1.254,4.129,1.254c4.096,0,7.417-3.319,7.417-7.413L28.034,19.63L28.034,19.63z" clipRule="evenodd"></path>
    <path fill="#81d4fa" fillRule="evenodd" d="M33.626,18.262v-0.854c-1.05,0.002-2.078-0.292-2.969-0.848 C31.445,17.423,32.483,18.018,33.626,18.262z M28.095,12.772c-0.027-0.153-0.047-0.306-0.061-0.461v-0.516h-4.036v16.019 c-0.006,1.867-1.523,3.379-3.393,3.379c-0.549,0-1.067-0.13-1.526-0.362c0.62,0.813,1.599,1.338,2.701,1.338 c1.87,0,3.386-1.512,3.393-3.379V12.772H28.095z M21.635,21.38v-0.909c-0.337-0.046-0.677-0.069-1.018-0.069 c-4.097,0-7.417,3.319-7.417,7.413c0,2.567,1.305,4.829,3.288,6.159c-1.308-1.336-2.114-3.165-2.114-5.183 C14.374,24.749,17.611,21.463,21.635,21.38z" clipRule="evenodd"></path>
  </svg>
)

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 48 48">
    <polygon fill="#ffc107" points="30.129,15.75 18.871,9.25 5.871,31.25 17.129,37.75"></polygon>
    <path fill="#1e88e5" d="M31.871,37.75c1.795,3.109,5.847,4.144,8.879,2.379c3.103-1.806,4.174-5.77,2.379-8.879l-13-22 c-1.795-3.109-5.835-4.144-8.879-2.379c-3.106,1.801-4.174,5.77-2.379,8.879L31.871,37.75z"></path>
    <circle cx="11.5" cy="34.5" r="6.5" fill="#43a047"></circle>
  </svg>
)

const MetaIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 48 48">
    <path fill="#0081fb" d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5 c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28 c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51 c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40 c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35 c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57 c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5 c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"></path>
    <linearGradient id="meta_g1" x1="42.304" x2="13.533" y1="24.75" y2="24.75" gradientUnits="userSpaceOnUse">
      <stop offset="0" stopColor="#0081fb"></stop><stop offset=".995" stopColor="#0064e1"></stop>
    </linearGradient>
    <path fill="url(#meta_g1)" d="M4.918,15.456 C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967 c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362 c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333 c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754 c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"></path>
  </svg>
)

const SnapchatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="27" height="27" viewBox="0 0 48 48">
    <path fill="#ffeb3b" d="M24,4C12.402,4,3,13.402,3,25c0,11.598,9.402,21,21,21s21-9.402,21-21C45,13.402,35.598,4,24,4z"></path>
    <path fill="#fff" d="M24,36c-2.451,0-4.045-1.09-4.827-1.612c-0.477-0.317-0.924-0.616-1.317-0.705 c-0.345-0.078-0.685-0.118-1.012-0.118c-0.59,0-1.056,0.103-1.386,0.175c-0.207,0.045-0.385,0.084-0.526,0.084 c-0.143,0-0.298-0.032-0.367-0.267c-0.064-0.218-0.107-0.428-0.148-0.631c-0.116-0.568-0.198-0.92-0.416-0.954 c-2.562-0.401-3.295-0.945-3.464-1.331c-0.024-0.055-0.037-0.11-0.04-0.165c-0.008-0.138,0.09-0.258,0.227-0.283 c3.731-0.614,5.399-4.440,5.467-4.589c0.004-0.009,0.008-0.017,0.012-0.026c0.225-0.456,0.268-0.851,0.131-1.174 c-0.258-0.611-1.099-0.879-1.657-1.058c-0.137-0.044-0.268-0.086-0.371-0.126c-0.504-0.198-1.349-0.612-1.241-1.186 c0.078-0.416,0.615-0.707,1.049-0.707c0.12,0,0.226,0.021,0.316,0.063c0.518,0.242,0.986,0.364,1.392,0.364 c0.558,0,0.802-0.236,0.833-0.266c-0.015-0.273-0.033-0.557-0.051-0.846c-0.119-1.922-0.268-4.313,0.337-5.674 C18.915,13.058,22.304,13,23.104,13h0.792c0.8,0,4.189,0.058,6.254,3.347c0.605,1.359,0.456,3.75,0.337,5.673 c-0.018,0.29-0.036,0.573-0.051,0.847c0.031,0.03,0.275,0.266,0.833,0.266c0.406,0,0.874-0.122,1.392-0.364 c0.09-0.042,0.196-0.063,0.316-0.063c0.433,0,0.971,0.291,1.049,0.707c0.108,0.574-0.737,0.988-1.241,1.186 c-0.103,0.04-0.234,0.082-0.371,0.126c-0.558,0.179-1.399,0.447-1.657,1.058c-0.137,0.323-0.094,0.718,0.131,1.174 c0.004,0.009,0.008,0.017,0.012,0.026c0.068,0.149,1.736,3.975,5.467,4.589c0.137,0.025,0.235,0.145,0.227,0.283 c-0.003,0.055-0.016,0.11-0.04,0.165c-0.169,0.386-0.902,0.93-3.464,1.331c-0.218,0.034-0.224,0.267-0.367,0.267 c-0.142,0-0.319-0.039-0.526-0.084 c-0.33-0.072-0.796-0.175-1.386-0.175c-0.327,0-0.667,0.04-1.012,0.118c-0.393,0.089-0.84,0.388-1.317,0.705 C28.045,34.91,26.451,36,24,36z"></path>
  </svg>
)

// ───────────────────────────── Helpers ─────────────────────────────
const getPlatformDisplayName = (platform) => {
  switch (platform) {
    case "meta": return "Meta"
    case "tiktok": return "TikTok"
    case "google": return "Google Ads"
    case "snapchat": return "Snapchat"
    default: return platform
  }
}

const getPlatformIcon = (platform) => {
  switch (platform) {
    case "meta": return <MetaIcon />
    case "tiktok": return <TikTokIcon />
    case "google": return <GoogleIcon />
    case "snapchat": return <SnapchatIcon />
    default: return <Globe size={16} />
  }
}

const getDefaultProvider = (platform) => {
  switch (platform) {
    case "meta": return "facebook"
    case "google": return "google"
    case "tiktok": return "tiktok"
    case "snapchat": return "snapchat"
    default: return ""
  }
}

// ───────────────────────────────────────── Component ─────────────────────────────────────────
const AdAccountFormModal = ({
  isOpen,
  onClose,
  accountId = null,
  isEditing = false,
  onSuccess,
}) => {
  const { currentUser } = useCurrentUser()
  // Any STAFF role picks the client the account belongs to; only a Client
  // account creates for itself. Previously this was a hardcoded
  // Admin/SuperAdmin list, so a custom staff role got no client picker and
  // the account was silently created against the staff member's own user id.
  const isAdmin = currentUser?.role?.name !== "Client"

  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = isEditing ? 1 : 3
  const [pendingAccountsCount] = useState(0)

  const [clients, setClients] = useState([])
  const [selectedClientId, setSelectedClientId] = useState("")
  const [allCreditAccounts, setAllCreditAccounts] = useState([])
  const [userCreditAccount, setUserCreditAccount] = useState(null)

  const [formData, setFormData] = useState({
    platform: "meta",
    provider: "facebook",
    currency: "USD",
    credit_account: "",
    accountName: "",
    accountAlias: "",
    timezone: "",
    category: "",
    link: "",
    idType: "bmId",          // will auto-adjust per platform
    bmIdOrEmail: "",
    pages: [""],             // visible only for Meta
  })

  const [errors, setErrors] = useState({})
  const [customCategory, setCustomCategory] = useState("")

  // ───────── Init/reset on open
  useEffect(() => {
    if (!isOpen) return

    if (accountId && isEditing) {
      fetchAccountData(accountId)
    } else {
      setFormData({
        platform: "meta",
        provider: "facebook",
        currency: "USD",
        credit_account: "",
        accountName: "",
        accountAlias: "",
        timezone: "",
        category: "",
        link: "",
        idType: "bmId",
        bmIdOrEmail: "",
        pages: [""],
      })
      setCurrentStep(1)
      setErrors({})
      setCustomCategory("")
      fetchCreditAccountsAndInit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, accountId, isEditing])

  // ───────── Keep provider in sync
  useEffect(() => {
    if (formData.platform) {
      setFormData((prev) => ({ ...prev, provider: getDefaultProvider(formData.platform) }))
    }
  }, [formData.platform])

  // ───────── Platform-specific rules from screenshot
  useEffect(() => {
    setErrors((e) => ({ ...e, bmIdOrEmail: "", pages: "", link: "" }))

    if (formData.platform === "meta") {
      // Meta: BM ID only, pages visible (optional)
      setFormData((prev) => ({
        ...prev,
        idType: "bmId",
        bmIdOrEmail: "",
        // keep pages as-is; users may enter 0..n
      }))
    } else if (formData.platform === "tiktok") {
      // TikTok: BC ID only, no pages
      setFormData((prev) => ({
        ...prev,
        idType: "bmId",    // keep same key for backend; label will say BC ID
        bmIdOrEmail: "",
        pages: [""],       // clear/hide; not sent
      }))
    } else if (formData.platform === "google" || formData.platform === "snapchat") {
      // Google/Snapchat: Email only, no pages
      setFormData((prev) => ({
        ...prev,
        idType: "email",
        bmIdOrEmail: "",
        pages: [""],       // clear/hide; not sent
      }))
    }
  }, [formData.platform])

  // ───────── When admin selects a client → bind their credit account
  useEffect(() => {
    if (!isAdmin) return
    if (!selectedClientId) {
      setUserCreditAccount(null)
      setFormData((prev) => ({ ...prev, credit_account: "" }))
      return
    }
    const acc = allCreditAccounts.find(a => a.user?._id === selectedClientId)
    setUserCreditAccount(acc || null)
    setFormData((prev) => ({ ...prev, credit_account: acc?._id || "" }))
  }, [selectedClientId, isAdmin, allCreditAccounts])

  // ───────── Fetch credit accounts and build client list / bind user account
  const fetchCreditAccountsAndInit = async () => {
    try {
      const res = await creditAccountsService.searchAccounts()
      const responseData = res.data
      if (!(responseData && responseData.statusCode === 200)) return

      const list = responseData.data || []
      setAllCreditAccounts(list)

      if (isAdmin) {
        const uniq = new Map()
        list.forEach(item => {
          if (item.user?._id && !uniq.has(item.user._id)) {
            uniq.set(item.user._id, {
              _id: item.user._id,
              fullName: item.user.reference || item.user.fullName || "Unnamed",
              email: item.user.email || "",
            })
          }
        })
        setClients(Array.from(uniq.values()))
        setSelectedClientId("")
        setUserCreditAccount(null)
      } else {
        const mine = list.find(a => a.user?._id === currentUser?._id)
        setUserCreditAccount(mine || null)
        setFormData(prev => ({ ...prev, credit_account: mine?._id || "" }))
        setSelectedClientId(currentUser?._id || "")
      }
    } catch (e) {
      console.error("Failed to fetch credit accounts:", e)
      toast.error(e.response?.data?.message || "Failed to load clients")
    }
  }

  // ───────── Load for edit mode
  const fetchAccountData = async (id) => {
    try {
      setLoading(true)
      const response = await adAccountsService.get(id)
      const d = response.data?.data
      if (response.data?.statusCode === 200 && d) {
        setFormData({
          platform: d.platform || "meta",
          provider: d.provider || getDefaultProvider(d.platform || "meta"),
          currency: "USD",
          credit_account: d.credit_account?._id || "",
          client: d.user?._id || "",
          accountName: d.account_name || "",
          accountAlias: d.account_alias || "",
          timezone: d.timezone || "",
          category: d.category || "",
          link: d.link || "",
          idType: d.bmId ? "bmId" : "email",
          bmIdOrEmail: d.bmId || d.email || "",
          pages: d.pages && d.pages.length > 0 ? d.pages : [""],
        })
        setSelectedClientId(d.user?._id || "")
        setUserCreditAccount(d.credit_account || null)
      }
      setLoading(false)
    } catch (e) {
      console.error("Failed to load account data:", e)
      setLoading(false)
    }
  }

  // ───────── Handlers / validation
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }))
  }

  const handlePageChange = (index, value) => {
    const newPages = [...formData.pages]
    newPages[index] = value
    handleChange("pages", newPages)
  }

  const addPageField = () => handleChange("pages", [...formData.pages, ""])
  const removePageField = (index) => {
    const newPages = formData.pages.filter((_, i) => i !== index)
    handleChange("pages", newPages.length ? newPages : [""])
  }

  const isValidUrl = (url) => {
    try {
      const withProto = /^https?:\/\//i.test(url) ? url : `http://${url}`
      new URL(withProto)
      return true
    } catch {
      return false
    }
  }

  const validateStep = (step) => {
    const newErrors = {}

    if (!isEditing) {
      if (step === 1) {
        if (isAdmin && !selectedClientId) newErrors.client = "Client is required"
        if (!formData.platform) newErrors.platform = "Platform is required"
        if (!userCreditAccount) {
          newErrors.credit_account = isAdmin
            ? "Selected client has no USD credit account"
            : "No USD credit account found for your user"
        }
      }
      if (step === 2) {
        if (!formData.timezone) newErrors.timezone = "Timezone is required"
        if (!formData.category) newErrors.category = "Category is required"
        else if (formData.category === "other" && !customCategory.trim()) {
          newErrors.customCategory = "Please enter a custom category"
        }
      }
      if (step === 3) {
        // Website URL required for all platforms
        if (!formData.link) newErrors.link = "Website URL is required"
        else if (!isValidUrl(formData.link)) newErrors.link = "Please enter a valid URL"

        // Pages:
        // - Meta: optional (validate only if provided)
        // - Others: hidden, ignored
        if (formData.platform === "meta") {
          const validPages = formData.pages.filter((p) => p.trim() !== "")
          if (validPages.length > 0) {
            const anyBad = validPages.some((p) => !isValidUrl(p))
            if (anyBad) newErrors.pages = "All page URLs must be valid"
          }
        }

        // ID field per platform:
        if (!formData.bmIdOrEmail) {
          if (formData.platform === "google" || formData.platform === "snapchat") {
            newErrors.bmIdOrEmail = "Email is required"
          } else if (formData.platform === "tiktok") {
            newErrors.bmIdOrEmail = "BC ID is required"
          } else {
            newErrors.bmIdOrEmail = "BM ID is required"
          }
        } else {
          if (formData.platform === "google" || formData.platform === "snapchat") {
            if (!/\S+@\S+\.\S+/.test(formData.bmIdOrEmail)) {
              newErrors.bmIdOrEmail = "Please enter a valid email address"
            }
          } else if (formData.platform === "tiktok") {
            if (!/^\d+$/.test(formData.bmIdOrEmail)) {
              newErrors.bmIdOrEmail = "BC ID should contain only numbers"
            }
          } else if (formData.platform === "meta") {
            if (!/^\d+$/.test(formData.bmIdOrEmail)) {
              newErrors.bmIdOrEmail = "BM ID should contain only numbers"
            }
          }
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const stepToValidate = isEditing ? 1 : currentStep
    if (!validateStep(stepToValidate)) return

    if (!isEditing && currentStep < totalSteps) {
      setCurrentStep((s) => s + 1)
      return
    }

    try {
      setProcessing(true)

      const filteredPages =
        formData.platform === "meta"
          ? formData.pages.filter((p) => p.trim() !== "")
          : [] // remove pages for TikTok/Google/Snapchat

      // use the typed custom category when "Other" is selected
      const effectiveCategory =
        formData.category === "other" ? customCategory.trim() : formData.category

      // lock idType based on platform rules
      const forcedIdType =
        formData.platform === "google" || formData.platform === "snapchat"
          ? "email"
          : "bmId" // meta bmId, tiktok "BC ID" but we send with bmId key

      const payload = {
        platform: formData.platform,
        accountName: formData.accountName,
        accountAlias: formData.accountAlias,
        currency: "USD",
        timezone: formData.timezone,
        category: effectiveCategory,
        link: formData.link,
        idType: forcedIdType,
        bmIdOrEmail: formData.bmIdOrEmail,
        client: isAdmin ? selectedClientId : currentUser?._id,
        credit_account: userCreditAccount?._id,
        pages: filteredPages,
      }

      const res = isEditing
        ? await adAccountsService.update(accountId, payload)
        : await adAccountsService.create(payload)

      if (res.data && (res.data.statusCode === 200 || res.data.statusCode === 201)) {
        toast.success(isEditing ? "Ad Account updated successfully" : "Ad Account created successfully")
        onSuccess && onSuccess()
      }

      onClose()
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred")
      console.error("Ad Account operation failed:", error)
    } finally {
      setProcessing(false)
    }
  }

  // ───────── Options
  const timezones = [
    { id: 1, name: "Select Timezone", value: "" },
    { id: 2, name: "America/Los_Angeles", value: "America/Los_Angeles" },
    { id: 3, name: "America/Denver", value: "America/Denver" },
    { id: 4, name: "Pacific/Honolulu", value: "Pacific/Honolulu" },
    { id: 5, name: "America/Anchorage", value: "America/Anchorage" },
    { id: 6, name: "America/Phoenix", value: "America/Phoenix" },
    { id: 7, name: "America/Chicago", value: "America/Chicago" },
    { id: 8, name: "America/New_York", value: "America/New_York" },
    { id: 9, name: "Asia/Dubai", value: "Asia/Dubai" },
    { id: 10, name: "America/Argentina/San_Luis", value: "America/Argentina/San_Luis" },
    { id: 11, name: "America/Argentina/Buenos_Aires", value: "America/Argentina/Buenos_Aires" },
    { id: 12, name: "America/Argentina/Salta", value: "America/Argentina/Salta" },
    { id: 13, name: "Europe/Vienna", value: "Europe/Vienna" },
    { id: 14, name: "Australia/Perth", value: "Australia/Perth" },
    { id: 15, name: "Australia/Broken_Hill", value: "Australia/Broken_Hill" },
    { id: 16, name: "Australia/Sydney", value: "Australia/Sydney" },
    { id: 17, name: "Europe/Sarajevo", value: "Europe/Sarajevo" },
    { id: 18, name: "Asia/Dhaka", value: "Asia/Dhaka" },
    { id: 19, name: "Europe/Brussels", value: "Europe/Brussels" },
    { id: 20, name: "Europe/Sofia", value: "Europe/Sofia" },
    { id: 21, name: "Asia/Bahrain", value: "Asia/Bahrain" },
    { id: 22, name: "America/La_Paz", value: "America/La_Paz" },
    { id: 23, name: "America/Noronha", value: "America/Noronha" },
    { id: 24, name: "America/Campo_Grande", value: "America/Campo_Grande" },
    { id: 25, name: "America/Belem", value: "America/Belem" },
    { id: 26, name: "America/Sao_Paulo", value: "America/Sao_Paulo" },
    { id: 27, name: "America/Nassau", value: "America/Nassau" },
    { id: 28, name: "America/Dawson", value: "America/Dawson" },
    { id: 29, name: "America/Vancouver", value: "America/Vancouver" },
    { id: 30, name: "America/Dawson_Creek", value: "America/Dawson_Creek" },
    { id: 31, name: "America/Edmonton", value: "America/Edmonton" },
    { id: 32, name: "America/Rainy_River", value: "America/Rainy_River" },
    { id: 33, name: "America/Regina", value: "America/Regina" },
    { id: 34, name: "America/Atikokan", value: "America/Atikokan" },
    { id: 35, name: "America/Iqaluit", value: "America/Iqaluit" },
    { id: 36, name: "America/Toronto", value: "America/Toronto" },
    { id: 37, name: "America/Blanc-Sablon", value: "America/Blanc-Sablon" },
    { id: 38, name: "America/Halifax", value: "America/Halifax" },
    { id: 39, name: "America/St_Johns", value: "America/St_Johns" },
    { id: 40, name: "Europe/Zurich", value: "Europe/Zurich" },
    { id: 41, name: "Pacific/Easter", value: "Pacific/Easter" },
    { id: 42, name: "America/Santiago", value: "America/Santiago" },
    { id: 43, name: "Asia/Shanghai", value: "Asia/Shanghai" },
    { id: 44, name: "America/Bogota", value: "America/Bogota" },
    { id: 45, name: "America/Costa_Rica", value: "America/Costa_Rica" },
    { id: 46, name: "Asia/Nicosia", value: "Asia/Nicosia" },
    { id: 47, name: "Europe/Prague", value: "Europe/Prague" },
    { id: 48, name: "Europe/Berlin", value: "Europe/Berlin" },
    { id: 49, name: "Europe/Copenhagen", value: "Europe/Copenhagen" },
    { id: 50, name: "America/Santo_Domingo", value: "America/Santo_Domingo" },
    { id: 51, name: "Pacific/Galapagos", value: "Pacific/Galapagos" },
    { id: 52, name: "America/Guayaquil", value: "America/Guayaquil" },
    { id: 53, name: "Europe/Tallinn", value: "Europe/Tallinn" },
    { id: 54, name: "Africa/Cairo", value: "Africa/Cairo" },
    { id: 55, name: "Atlantic/Canary", value: "Atlantic/Canary" },
    { id: 56, name: "Europe/Madrid", value: "Europe/Madrid" },
    { id: 57, name: "Europe/Helsinki", value: "Europe/Helsinki" },
    { id: 58, name: "Europe/Paris", value: "Europe/Paris" },
    { id: 59, name: "Europe/London", value: "Europe/London" },
    { id: 60, name: "Africa/Accra", value: "Africa/Accra" },
    { id: 61, name: "Europe/Athens", value: "Europe/Athens" },
    { id: 62, name: "America/Guatemala", value: "America/Guatemala" },
    { id: 63, name: "Asia/Hong_Kong", value: "Asia/Hong_Kong" },
    { id: 64, name: "America/Tegucigalpa", value: "America/Tegucigalpa" },
    { id: 65, name: "Europe/Zagreb", value: "Europe/Zagreb" },
    { id: 66, name: "Europe/Budapest", value: "Europe/Budapest" },
    { id: 67, name: "Asia/Jakarta", value: "Asia/Jakarta" },
    { id: 68, name: "Asia/Makassar", value: "Asia/Makassar" },
    { id: 69, name: "Asia/Jayapura", value: "Asia/Jayapura" },
    { id: 70, name: "Europe/Dublin", value: "Europe/Dublin" },
    { id: 71, name: "Asia/Jerusalem", value: "Asia/Jerusalem" },
    { id: 72, name: "Asia/Kolkata", value: "Asia/Kolkata" },
    { id: 73, name: "Asia/Baghdad", value: "Asia/Baghdad" },
    { id: 74, name: "Atlantic/Reykjavik", value: "Atlantic/Reykjavik" },
    { id: 75, name: "Europe/Rome", value: "Europe/Rome" },
    { id: 76, name: "America/Jamaica", value: "America/Jamaica" },
    { id: 77, name: "Asia/Amman", value: "Asia/Amman" },
    { id: 78, name: "Asia/Tokyo", value: "Asia/Tokyo" },
    { id: 79, name: "Africa/Nairobi", value: "Africa/Nairobi" },
    { id: 80, name: "Asia/Seoul", value: "Asia/Seoul" },
    { id: 81, name: "Asia/Kuwait", value: "Asia/Kuwait" },
    { id: 82, name: "Asia/Beirut", value: "Asia/Beirut" },
    { id: 83, name: "Asia/Colombo", value: "Asia/Colombo" },
    { id: 84, name: "Europe/Vilnius", value: "Europe/Vilnius" },
    { id: 85, name: "Europe/Luxembourg", value: "Europe/Luxembourg" },
    { id: 86, name: "Europe/Riga", value: "Europe/Riga" },
    { id: 87, name: "Africa/Casablanca", value: "Africa/Casablanca" },
    { id: 88, name: "Europe/Skopje", value: "Europe/Skopje" },
    { id: 89, name: "Europe/Malta", value: "Europe/Malta" },
    { id: 90, name: "Indian/Mauritius", value: "Indian/Mauritius" },
    { id: 91, name: "Indian/Maldives", value: "Indian/Maldives" },
    { id: 92, name: "America/Tijuana", value: "America/Tijuana" },
    { id: 93, name: "America/Hermosillo", value: "America/Hermosillo" },
    { id: 94, name: "America/Mazatlan", value: "America/Mazatlan" },
    { id: 95, name: "America/Mexico_City", value: "America/Mexico_City" },
    { id: 96, name: "Asia/Kuala_Lumpur", value: "Asia/Kuala_Lumpur" },
    { id: 97, name: "Africa/Lagos", value: "Africa/Lagos" },
    { id: 98, name: "America/Managua", value: "America/Managua" },
    { id: 99, name: "Europe/Amsterdam", value: "Europe/Amsterdam" },
    { id: 100, name: "Europe/Oslo", value: "Europe/Oslo" },
    { id: 101, name: "Pacific/Auckland", value: "Pacific/Auckland" },
    { id: 102, name: "Asia/Muscat", value: "Asia/Muscat" },
    { id: 103, name: "America/Panama", value: "America/Panama" },
    { id: 104, name: "America/Lima", value: "America/Lima" },
    { id: 105, name: "Asia/Manila", value: "Asia/Manila" },
    { id: 106, name: "Asia/Karachi", value: "Asia/Karachi" },
    { id: 107, name: "Europe/Warsaw", value: "Europe/Warsaw" },
    { id: 108, name: "America/Puerto_Rico", value: "America/Puerto_Rico" },
    { id: 109, name: "Asia/Gaza", value: "Asia/Gaza" },
    { id: 110, name: "Atlantic/Azores", value: "Atlantic/Azores" },
    { id: 111, name: "Europe/Lisbon", value: "Europe/Lisbon" },
    { id: 112, name: "America/Asuncion", value: "America/Asuncion" },
    { id: 113, name: "Asia/Qatar", value: "Asia/Qatar" },
    { id: 114, name: "Europe/Bucharest", value: "Europe/Bucharest" },
    { id: 115, name: "Europe/Belgrade", value: "Europe/Belgrade" },
    { id: 116, name: "Europe/Kaliningrad", value: "Europe/Kaliningrad" },
    { id: 117, name: "Europe/Moscow", value: "Europe/Moscow" },
    { id: 118, name: "Europe/Samara", value: "Europe/Samara" },
    { id: 119, name: "Asia/Yekaterinburg", value: "Asia/Yekaterinburg" },
    { id: 120, name: "Asia/Omsk", value: "Asia/Omsk" },
    { id: 121, name: "Asia/Krasnoyarsk", value: "Asia/Krasnoyarsk" },
    { id: 122, name: "Asia/Irkutsk", value: "Asia/Irkutsk" },
    { id: 123, name: "Asia/Yakutsk", value: "Asia/Yakutsk" },
    { id: 124, name: "Asia/Vladivostok", value: "Asia/Vladivostok" },
    { id: 125, name: "Asia/Magadan", value: "Asia/Magadan" },
    { id: 126, name: "Asia/Kamchatka", value: "Asia/Kamchatka" },
    { id: 127, name: "Asia/Riyadh", value: "Asia/Riyadh" },
    { id: 128, name: "Europe/Stockholm", value: "Europe/Stockholm" },
    { id: 129, name: "Asia/Singapore", value: "Asia/Singapore" },
    { id: 130, name: "Europe/Ljubljana", value: "Europe/Ljubljana" },
    { id: 131, name: "Europe/Bratislava", value: "Europe/Bratislava" },
    { id: 132, name: "America/El_Salvador", value: "America/El_Salvador" },
    { id: 133, name: "Asia/Bangkok", value: "Asia/Bangkok" },
    { id: 134, name: "Africa/Tunis", value: "Africa/Tunis" },
    { id: 135, name: "Europe/Istanbul", value: "Europe/Istanbul" },
    { id: 136, name: "America/Port_of_Spain", value: "America/Port_of_Spain" },
    { id: 137, name: "Asia/Taipei", value: "Asia/Taipei" },
    { id: 138, name: "Europe/Kiev", value: "Europe/Kiev" },
    { id: 139, name: "America/Montevideo", value: "America/Montevideo" },
    { id: 140, name: "America/Caracas", value: "America/Caracas" },
    { id: 141, name: "Asia/Ho_Chi_Minh", value: "Asia/Ho_Chi_Minh" },
    { id: 142, name: "Africa/Johannesburg", value: "Africa/Johannesburg" },
    { id: 143, name: "America/Winnipeg", value: "America/Winnipeg" },
    { id: 144, name: "America/Detroit", value: "America/Detroit" },
    { id: 145, name: "Australia/Melbourne", value: "Australia/Melbourne" },
    { id: 146, name: "Asia/Kathmandu", value: "Asia/Kathmandu" },
    { id: 147, name: "Asia/Baku", value: "Asia/Baku" },
    { id: 148, name: "Africa/Abidjan", value: "Africa/Abidjan" },
    { id: 149, name: "Africa/Addis_Ababa", value: "Africa/Addis_Ababa" },
    { id: 150, name: "Africa/Algiers", value: "Africa/Algiers" },
    { id: 151, name: "Africa/Asmara", value: "Africa/Asmara" },
    { id: 152, name: "Africa/Bamako", value: "Africa/Bamako" },
    { id: 153, name: "Africa/Bangui", value: "Africa/Bangui" },
    { id: 154, name: "Africa/Banjul", value: "Africa/Banjul" },
    { id: 155, name: "Africa/Bissau", value: "Africa/Bissau" },
    { id: 156, name: "Africa/Blantyre", value: "Africa/Blantyre" },
    { id: 157, name: "Africa/Brazzaville", value: "Africa/Brazzaville" },
    { id: 158, name: "Africa/Bujumbura", value: "Africa/Bujumbura" },
    { id: 159, name: "Africa/Ceuta", value: "Africa/Ceuta" },
    { id: 160, name: "Africa/Conakry", value: "Africa/Conakry" },
    { id: 161, name: "Africa/Dakar", value: "Africa/Dakar" },
    { id: 162, name: "Africa/Dar_es_Salaam", value: "Africa/Dar_es_Salaam" },
    { id: 163, name: "Africa/Djibouti", value: "Africa/Djibouti" },
    { id: 164, name: "Africa/Douala", value: "Africa/Douala" },
    { id: 165, name: "Africa/El_Aaiun", value: "Africa/El_Aaiun" },
    { id: 166, name: "Africa/Freetown", value: "Africa/Freetown" },
    { id: 167, name: "Africa/Gaborone", value: "Africa/Gaborone" },
    { id: 168, name: "Africa/Harare", value: "Africa/Harare" },
    { id: 169, name: "Africa/Juba", value: "Africa/Juba" },
    { id: 170, name: "Africa/Kampala", value: "Africa/Kampala" },
    { id: 171, name: "Africa/Khartoum", value: "Africa/Khartoum" },
    { id: 172, name: "Africa/Kigali", value: "Africa/Kigali" },
    { id: 173, name: "Africa/Kinshasa", value: "Africa/Kinshasa" },
    { id: 174, name: "Africa/Libreville", value: "Africa/Libreville" },
    { id: 175, name: "Africa/Lome", value: "Africa/Lome" },
    { id: 176, name: "Africa/Luanda", value: "Africa/Luanda" },
    { id: 177, name: "Africa/Lubumbashi", value: "Africa/Lubumbashi" },
    { id: 178, name: "Africa/Lusaka", value: "Africa/Lusaka" },
    { id: 179, name: "Africa/Malabo", value: "Africa/Malabo" },
    { id: 180, name: "Africa/Maputo", value: "Africa/Maputo" },
    { id: 181, name: "Africa/Maseru", value: "Africa/Maseru" },
    { id: 182, name: "Africa/Mbabane", value: "Africa/Mbabane" },
    { id: 183, name: "Africa/Mogadishu", value: "Africa/Mogadishu" },
    { id: 184, name: "Africa/Monrovia", value: "Africa/Monrovia" },
    { id: 185, name: "Africa/Ndjamena", value: "Africa/Ndjamena" },
    { id: 186, name: "Africa/Niamey", value: "Africa/Niamey" },
    { id: 187, name: "Africa/Nouakchott", value: "Africa/Nouakchott" },
    { id: 188, name: "Africa/Ouagadougou", value: "Africa/Ouagadougou" },
    { id: 189, name: "Africa/Porto-Novo", value: "Africa/Porto-Novo" },
    { id: 190, name: "Africa/Sao_Tome", value: "Africa/Sao_Tome" },
    { id: 191, name: "Africa/Tripoli", value: "Africa/Tripoli" },
    { id: 192, name: "Africa/Windhoek", value: "Africa/Windhoek" },
    { id: 193, name: "America/Adak", value: "America/Adak" },
    { id: 194, name: "America/Anguilla", value: "America/Anguilla" },
    { id: 195, name: "America/Antigua", value: "America/Antigua" },
    { id: 196, name: "America/Araguaina", value: "America/Araguaina" },
    { id: 197, name: "America/Argentina/Catamarca", value: "America/Argentina/Catamarca" },
    { id: 198, name: "America/Argentina/Cordoba", value: "America/Argentina/Cordoba" },
    { id: 199, name: "America/Argentina/Jujuy", value: "America/Argentina/Jujuy" },
    { id: 200, name: "America/Argentina/La_Rioja", value: "America/Argentina/La_Rioja" },
    { id: 201, name: "America/Argentina/Mendoza", value: "America/Argentina/Mendoza" },
    { id: 202, name: "America/Argentina/Rio_Gallegos", value: "America/Argentina/Rio_Gallegos" },
    { id: 203, name: "America/Argentina/San_Juan", value: "America/Argentina/San_Juan" },
    { id: 204, name: "America/Argentina/Tucuman", value: "America/Argentina/Tucuman" },
    { id: 205, name: "America/Argentina/Ushuaia", value: "America/Argentina/Ushuaia" },
    { id: 206, name: "America/Aruba", value: "America/Aruba" },
    { id: 207, name: "America/Bahia", value: "America/Bahia" },
    { id: 208, name: "America/Bahia_Banderas", value: "America/Bahia_Banderas" },
    { id: 209, name: "America/Barbados", value: "America/Barbados" },
    { id: 210, name: "America/Belize", value: "America/Belize" },
    { id: 211, name: "America/Boa_Vista", value: "America/Boa_Vista" },
    { id: 212, name: "America/Boise", value: "America/Boise" },
    { id: 213, name: "America/Cambridge_Bay", value: "America/Cambridge_Bay" },
    { id: 214, name: "America/Cancun", value: "America/Cancun" },
    { id: 215, name: "America/Cayenne", value: "America/Cayenne" },
    { id: 216, name: "America/Cayman", value: "America/Cayman" },
    { id: 217, name: "America/Chihuahua", value: "America/Chihuahua" },
    { id: 218, name: "America/Creston", value: "America/Creston" },
    { id: 219, name: "America/Cuiaba", value: "America/Cuiaba" },
    { id: 220, name: "America/Curacao", value: "America/Curacao" },
    { id: 221, name: "America/Danmarkshavn", value: "America/Danmarkshavn" },
    { id: 222, name: "America/Dominica", value: "America/Dominica" },
    { id: 223, name: "America/Eirunepe", value: "America/Eirunepe" },
    { id: 224, name: "America/Fort_Nelson", value: "America/Fort_Nelson" },
    { id: 225, name: "America/Fortaleza", value: "America/Fortaleza" },
    { id: 226, name: "America/Glace_Bay", value: "America/Glace_Bay" },
    { id: 227, name: "America/Godthab", value: "America/Godthab" },
    { id: 228, name: "America/Goose_Bay", value: "America/Goose_Bay" },
    { id: 229, name: "America/Grand_Turk", value: "America/Grand_Turk" },
    { id: 230, name: "America/Grenada", value: "America/Grenada" },
    { id: 231, name: "America/Guadeloupe", value: "America/Guadeloupe" },
    { id: 232, name: "America/Guyana", value: "America/Guyana" },
    { id: 233, name: "America/Havana", value: "America/Havana" },
    { id: 234, name: "America/Indiana/Indianapolis", value: "America/Indiana/Indianapolis" },
    { id: 235, name: "America/Indiana/Knox", value: "America/Indiana/Knox" },
    { id: 236, name: "America/Indiana/Marengo", value: "America/Indiana/Marengo" },
    { id: 237, name: "America/Indiana/Petersburg", value: "America/Indiana/Petersburg" },
    { id: 238, name: "America/Indiana/Tell_City", value: "America/Indiana/Tell_City" },
    { id: 239, name: "America/Indiana/Vevay", value: "America/Indiana/Vevay" },
    { id: 240, name: "America/Indiana/Vincennes", value: "America/Indiana/Vincennes" },
    { id: 241, name: "America/Indiana/Winamac", value: "America/Indiana/Winamac" },
    { id: 242, name: "America/Indianapolis", value: "America/Indianapolis" },
    { id: 243, name: "America/Inuvik", value: "America/Inuvik" },
    { id: 244, name: "America/Juneau", value: "America/Juneau" },
    { id: 245, name: "America/Kentucky/Louisville", value: "America/Kentucky/Louisville" },
    { id: 246, name: "America/Kentucky/Monticello", value: "America/Kentucky/Monticello" },
    { id: 247, name: "America/Kralendijk", value: "America/Kralendijk" },
    { id: 248, name: "America/Lower_Princes", value: "America/Lower_Princes" },
    { id: 249, name: "America/Maceio", value: "America/Maceio" },
    { id: 250, name: "America/Manaus", value: "America/Manaus" },
    { id: 251, name: "America/Marigot", value: "America/Marigot" },
    { id: 252, name: "America/Martinique", value: "America/Martinique" },
    { id: 253, name: "America/Matamoros", value: "America/Matamoros" },
    { id: 254, name: "America/Menominee", value: "America/Menominee" },
    { id: 255, name: "America/Merida", value: "America/Merida" },
    { id: 256, name: "America/Metlakatla", value: "America/Metlakatla" },
    { id: 257, name: "America/Miquelon", value: "America/Miquelon" },
    { id: 258, name: "America/Moncton", value: "America/Moncton" },
    { id: 259, name: "America/Monterrey", value: "America/Monterrey" },
    { id: 260, name: "America/Montreal", value: "America/Montreal" },
    { id: 261, name: "America/Montserrat", value: "America/Montserrat" },
    { id: 262, name: "America/Nipigon", value: "America/Nipigon" },
    { id: 263, name: "America/Nome", value: "America/Nome" },
    { id: 264, name: "America/North_Dakota/Beulah", value: "America/North_Dakota/Beulah" },
    { id: 265, name: "America/North_Dakota/Center", value: "America/North_Dakota/Center" },
    { id: 266, name: "America/North_Dakota/New_Salem", value: "America/North_Dakota/New_Salem" },
    { id: 267, name: "America/Ojinaga", value: "America/Ojinaga" },
    { id: 268, name: "America/Pangnirtung", value: "America/Pangnirtung" },
    { id: 269, name: "America/Paramaribo", value: "America/Paramaribo" },
    { id: 270, name: "America/Port-au-Prince", value: "America/Port-au-Prince" },
    { id: 271, name: "America/Porto_Velho", value: "America/Porto_Velho" },
    { id: 272, name: "America/Punta_Arenas", value: "America/Punta_Arenas" },
    { id: 273, name: "America/Rankin_Inlet", value: "America/Rankin_Inlet" },
    { id: 274, name: "America/Recife", value: "America/Recife" },
    { id: 275, name: "America/Resolute", value: "America/Resolute" },
    { id: 276, name: "America/Rio_Branco", value: "America/Rio_Branco" },
    { id: 277, name: "America/Santarem", value: "America/Santarem" },
    { id: 278, name: "America/Scoresbysund", value: "America/Scoresbysund" },
    { id: 279, name: "America/Sitka", value: "America/Sitka" },
    { id: 280, name: "America/St_Barthelemy", value: "America/St_Barthelemy" },
    { id: 281, name: "America/St_Kitts", value: "America/St_Kitts" },
    { id: 282, name: "America/St_Lucia", value: "America/St_Lucia" },
    { id: 283, name: "America/St_Thomas", value: "America/St_Thomas" },
    { id: 284, name: "America/St_Vincent", value: "America/St_Vincent" },
    { id: 285, name: "America/Swift_Current", value: "America/Swift_Current" },
    { id: 286, name: "America/Thule", value: "America/Thule" },
    { id: 287, name: "America/Thunder_Bay", value: "America/Thunder_Bay" },
    { id: 288, name: "America/Tortola", value: "America/Tortola" },
    { id: 289, name: "America/Whitehorse", value: "America/Whitehorse" },
    { id: 290, name: "America/Yakutat", value: "America/Yakutat" },
    { id: 291, name: "America/Yellowknife", value: "America/Yellowknife" },
    { id: 292, name: "Antarctica/Casey", value: "Antarctica/Casey" },
    { id: 293, name: "Antarctica/Davis", value: "Antarctica/Davis" },
    { id: 294, name: "Antarctica/DumontDUrville", value: "Antarctica/DumontDUrville" },
    { id: 295, name: "Antarctica/Macquarie", value: "Antarctica/Macquarie" },
    { id: 296, name: "Antarctica/Mawson", value: "Antarctica/Mawson" },
    { id: 297, name: "Antarctica/McMurdo", value: "Antarctica/McMurdo" },
    { id: 298, name: "Antarctica/Palmer", value: "Antarctica/Palmer" },
    { id: 299, name: "Antarctica/Rothera", value: "Antarctica/Rothera" },
    { id: 300, name: "Antarctica/Syowa", value: "Antarctica/Syowa" },
    { id: 301, name: "Antarctica/Troll", value: "Antarctica/Troll" },
    { id: 302, name: "Antarctica/Vostok", value: "Antarctica/Vostok" },
    { id: 303, name: "Arctic/Longyearbyen", value: "Arctic/Longyearbyen" },
    { id: 304, name: "Asia/Aden", value: "Asia/Aden" },
    { id: 305, name: "Asia/Almaty", value: "Asia/Almaty" },
    { id: 306, name: "Asia/Anadyr", value: "Asia/Anadyr" },
    { id: 307, name: "Asia/Aqtau", value: "Asia/Aqtau" },
    { id: 308, name: "Asia/Aqtobe", value: "Asia/Aqtobe" },
    { id: 309, name: "Asia/Ashgabat", value: "Asia/Ashgabat" },
    { id: 310, name: "Asia/Atyrau", value: "Asia/Atyrau" },
    { id: 311, name: "Asia/Barnaul", value: "Asia/Barnaul" },
    { id: 312, name: "Asia/Bishkek", value: "Asia/Bishkek" },
    { id: 313, name: "Asia/Brunei", value: "Asia/Brunei" },
    { id: 314, name: "Asia/Chita", value: "Asia/Chita" },
    { id: 315, name: "Asia/Choibalsan", value: "Asia/Choibalsan" },
    { id: 316, name: "Asia/Damascus", value: "Asia/Damascus" },
    { id: 317, name: "Asia/Dili", value: "Asia/Dili" },
    { id: 318, name: "Asia/Dushanbe", value: "Asia/Dushanbe" },
    { id: 319, name: "Asia/Famagusta", value: "Asia/Famagusta" },
    { id: 320, name: "Asia/Hebron", value: "Asia/Hebron" },
    { id: 321, name: "Asia/Hovd", value: "Asia/Hovd" },
    { id: 322, name: "Asia/Istanbul", value: "Asia/Istanbul" },
    { id: 323, name: "Asia/Kabul", value: "Asia/Kabul" },
    { id: 324, name: "Asia/Khandyga", value: "Asia/Khandyga" },
    { id: 325, name: "Asia/Kuching", value: "Asia/Kuching" },
    { id: 326, name: "Asia/Macau", value: "Asia/Macau" },
    { id: 327, name: "Asia/Novokuznetsk", value: "Asia/Novokuznetsk" },
    { id: 328, name: "Asia/Novosibirsk", value: "Asia/Novosibirsk" },
    { id: 329, name: "Asia/Oral", value: "Asia/Oral" },
    { id: 330, name: "Asia/Phnom_Penh", value: "Asia/Phnom_Penh" },
    { id: 331, name: "Asia/Pontianak", value: "Asia/Pontianak" },
    { id: 332, name: "Asia/Pyongyang", value: "Asia/Pyongyang" },
    { id: 333, name: "Asia/Qostanay", value: "Asia/Qostanay" },
    { id: 334, name: "Asia/Qyzylorda", value: "Asia/Qyzylorda" },
    { id: 335, name: "Asia/Sakhalin", value: "Asia/Sakhalin" },
    { id: 336, name: "Asia/Samarkand", value: "Asia/Samarkand" },
    { id: 337, name: "Asia/Srednekolymsk", value: "Asia/Srednekolymsk" },
    { id: 338, name: "Asia/Tashkent", value: "Asia/Tashkent" },
    { id: 339, name: "Asia/Tbilisi", value: "Asia/Tbilisi" },
    { id: 340, name: "Asia/Tehran", value: "Asia/Tehran" },
    { id: 341, name: "Asia/Thimphu", value: "Asia/Thimphu" },
    { id: 342, name: "Asia/Tomsk", value: "Asia/Tomsk" },
    { id: 343, name: "Asia/Ulaanbaatar", value: "Asia/Ulaanbaatar" },
    { id: 344, name: "Asia/Urumqi", value: "Asia/Urumqi" },
    { id: 345, name: "Asia/Ust-Nera", value: "Asia/Ust-Nera" },
    { id: 346, name: "Asia/Vientiane", value: "Asia/Vientiane" },
    { id: 347, name: "Asia/Yangon", value: "Asia/Yangon" },
    { id: 348, name: "Asia/Yerevan", value: "Asia/Yerevan" },
    { id: 349, name: "Atlantic/Bermuda", value: "Atlantic/Bermuda" },
    { id: 350, name: "Atlantic/Cape_Verde", value: "Atlantic/Cape_Verde" },
    { id: 351, name: "Atlantic/Faroe", value: "Atlantic/Faroe" },
    { id: 352, name: "Atlantic/Madeira", value: "Atlantic/Madeira" },
    { id: 353, name: "Atlantic/South_Georgia", value: "Atlantic/South_Georgia" },
    { id: 354, name: "Atlantic/St_Helena", value: "Atlantic/St_Helena" },
    { id: 355, name: "Atlantic/Stanley", value: "Atlantic/Stanley" },
    { id: 356, name: "Australia/Adelaide", value: "Australia/Adelaide" },
    { id: 357, name: "Australia/Brisbane", value: "Australia/Brisbane" },
    { id: 358, name: "Australia/Currie", value: "Australia/Currie" },
    { id: 359, name: "Australia/Darwin", value: "Australia/Darwin" },
    { id: 360, name: "Australia/Eucla", value: "Australia/Eucla" },
    { id: 361, name: "Australia/Hobart", value: "Australia/Hobart" },
    { id: 362, name: "Australia/Lindeman", value: "Australia/Lindeman" },
    { id: 363, name: "Australia/Lord_Howe", value: "Australia/Lord_Howe" },
    { id: 364, name: "CET", value: "CET" },
    { id: 365, name: "CST6CDT", value: "CST6CDT" },
    { id: 366, name: "EET", value: "EET" },
    { id: 367, name: "EST", value: "EST" },
    { id: 368, name: "EST5EDT", value: "EST5EDT" },
    { id: 369, name: "Etc/GMT", value: "Etc/GMT" },
    { id: 370, name: "Etc/GMT+0", value: "Etc/GMT+0" },
    { id: 371, name: "Etc/GMT+1", value: "Etc/GMT+1" },
    { id: 372, name: "Etc/GMT+10", value: "Etc/GMT+10" },
    { id: 373, name: "Etc/GMT+11", value: "Etc/GMT+11" },
    { id: 374, name: "Etc/GMT+12", value: "Etc/GMT+12" },
    { id: 375, name: "Etc/GMT+2", value: "Etc/GMT+2" },
    { id: 376, name: "Etc/GMT+3", value: "Etc/GMT+3" },
    { id: 377, name: "Etc/GMT+4", value: "Etc/GMT+4" },
    { id: 378, name: "Etc/GMT+5", value: "Etc/GMT+5" },
    { id: 379, name: "Etc/GMT+6", value: "Etc/GMT+6" },
    { id: 380, name: "Etc/GMT+7", value: "Etc/GMT+7" },
    { id: 381, name: "Etc/GMT+8", value: "Etc/GMT+8" },
    { id: 382, name: "Etc/GMT+9", value: "Etc/GMT+9" },
    { id: 383, name: "Etc/GMT-0", value: "Etc/GMT-0" },
    { id: 384, name: "Etc/GMT-1", value: "Etc/GMT-1" },
    { id: 385, name: "Etc/GMT-10", value: "Etc/GMT-10" },
    { id: 386, name: "Etc/GMT-11", value: "Etc/GMT-11" },
    { id: 387, name: "Etc/GMT-12", value: "Etc/GMT-12" },
    { id: 388, name: "Etc/GMT-13", value: "Etc/GMT-13" },
    { id: 389, name: "Etc/GMT-14", value: "Etc/GMT-14" },
    { id: 390, name: "Etc/GMT-2", value: "Etc/GMT-2" },
    { id: 391, name: "Etc/GMT-3", value: "Etc/GMT-3" },
    { id: 392, name: "Etc/GMT-4", value: "Etc/GMT-4" },
    { id: 393, name: "Etc/GMT-5", value: "Etc/GMT-5" },
    { id: 394, name: "Etc/GMT-6", value: "Etc/GMT-6" },
    { id: 395, name: "Etc/GMT-7", value: "Etc/GMT-7" },
    { id: 396, name: "Etc/GMT-8", value: "Etc/GMT-8" },
    { id: 397, name: "Etc/GMT-9", value: "Etc/GMT-9" },
    { id: 398, name: "Etc/GMT0", value: "Etc/GMT0" },
    { id: 399, name: "Etc/Greenwich", value: "Etc/Greenwich" },
    { id: 400, name: "Etc/Universal", value: "Etc/Universal" },

  ]

  const categories = [
    { id: 1, name: "Select Category", value: "" },
    { id: 2, name: "E-commerce", value: "ecommerce" },
    { id: 3, name: "Finance", value: "finance" },
    { id: 4, name: "Education", value: "education" },
    { id: 5, name: "Entertainment", value: "entertainment" },
    { id: 6, name: "Health & Fitness", value: "health" },
    { id: 7, name: "Technology", value: "technology" },
    { id: 8, name: "Travel", value: "travel" },
    { id: 9, name: "Food & Beverage", value: "food" },
    { id: 10, name: "Real Estate", value: "realestate" },
    { id: 11, name: "Other (custom)", value: "other" },
  ]

  const clientOptions = [
    { id: "_placeholder", name: "Select Client", value: "" },
    ...clients.map(c => ({ id: c._id, name: `${c.fullName}${c.email ? ` • ${c.email}` : ""}`, value: c._id }))
  ]

  // Convenience flags
  const showPages = formData.platform === "meta"
  const idLabel =
    formData.platform === "tiktok" ? "BC ID" :
      formData.platform === "meta" ? "BM ID" :
        "Email"

  const idIcon =
    formData.platform === "google" || formData.platform === "snapchat"
      ? <Mail size={16} className="text-neutral-500 dark:text-neutral-400" />
      : <Hash size={16} className="text-neutral-500 dark:text-neutral-400" />

  // ───────────────────────────────────────── UI ─────────────────────────────────────────
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-xl transform overflow-visible rounded-2xl bg-white dark:bg-neutral-950 shadow-2xl transition-all  dark:bg-neutral-900/30" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                {isEditing ? (
                  <div className="relative border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                    <div className="flex items-center justify-between">
                      <Dialog.Title className="text-xl font-[400] text-neutral-900 dark:text-white">Edit Ad Account</Dialog.Title>
                      <button onClick={onClose} className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors" type="button" aria-label="Close">
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative rounded-t-2xl bg-gradient-to-r from-neutral-900 border border-neutral-900 to-neutral-800 dark:from-black dark:to-neutral-900 p-6">
                    <div className="flex items-center justify-between">
                      <Dialog.Title className="text-lg font-semibold text-white">Get New Ad Account</Dialog.Title>
                      <button onClick={onClose} className="p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors" type="button" aria-label="Close">
                        <X size={20} />
                      </button>
                    </div>
                    <div className="mt-6 flex items-center gap-2">
                      {Array.from({ length: totalSteps }).map((_, i) => (
                        <div key={i} className={`h-1.5 rounded-full transition-all ${i + 1 <= currentStep ? "bg-white flex-grow" : "bg-white/30 w-6"}`} />
                      ))}
                    </div>
                    <div className="mt-2 text-white/90 text-xs">
                      {currentStep === 1 && "Step 1: Select Client & Platform"}
                      {currentStep === 2 && "Step 2: Account Details"}
                      {currentStep === 3 && "Step 3: Additional Information"}
                    </div>
                  </div>
                )}

                {/* Body */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <form onSubmit={(e) => e.preventDefault()}>
                      {/* Pending notice */}
                      {!isEditing && pendingAccountsCount > 2 && (
                        <div className="mb-6 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl flex items-start gap-2">
                          <AlertTriangle className="text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" size={16} />
                          <span className="text-xs text-amber-800 dark:text-amber-300">
                            You're reaching your pending account limit ({pendingAccountsCount}/5). Contact support for assistance.
                          </span>
                        </div>
                      )}

                      {/* Edit Mode */}
                      {isEditing && (
                        <div className="space-y-6">
                          <div className="mb-6">
                            <InputCustom
                              label="Account Alias (Optional)"
                              placeholder="Enter account alias"
                              value={formData.accountAlias}
                              onChange={(v) => handleChange("accountAlias", v)}
                              icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
                            />
                            <div className="mt-4">
                              <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                                Credit Account
                              </label>
                              {userCreditAccount ? (
                                <div className="p-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                                      <CreditCard size={16} className="text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-gray-800 dark:text-white">
                                        {userCreditAccount.account_name}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        USD • {userCreditAccount.user?.fullName || ""}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-3 border border-red-200 dark:border-red-800 rounded-xl bg-red-50 dark:bg-red-900/20">
                                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                    <span className="text-sm">No USD credit account found. Please contact support.</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 pt-6">
                            <div className="flex items-center mb-4">
                              <Lock size={16} className="text-neutral-500 mr-2" />
                              <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                                Account Information (Read-only)
                              </h3>
                            </div>

                            <div className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 rounded-xl border border-neutral-200  p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 rounded-full bg-neutral-100 dark:border-neutral-900 dark:bg-neutral-900/30 flex items-center justify-center mr-3">
                                    {getPlatformIcon(formData.platform)}
                                  </div>
                                  <div>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Platform</p>
                                    <p className="text-sm font-[400] text-neutral-800 dark:text-white">
                                      {getPlatformDisplayName(formData.platform)}
                                    </p>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Currency</p>
                                  <p className="text-sm font-[400] text-neutral-800 dark:text-white">USD</p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Account Name</p>
                                  <p className="text-sm font-[400] text-neutral-800 dark:text-white">
                                    {formData.accountName}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Timezone</p>
                                  <p className="text-sm font-[400] text-neutral-800 dark:text-white">
                                    {formData.timezone || "—"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Category</p>
                                  <p className="text-sm font-[400] text-neutral-800 dark:text-white">
                                    {formData.category === "other"
                                      ? (customCategory.trim() || "—")
                                      : (categories.find((c) => c.value === formData.category)?.name || "—")}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Website URL</p>
                                  <p className="text-sm font-[400] text-neutral-800 dark:text-white truncate">
                                    {formData.link || "—"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                    {formData.platform === "tiktok" ? "BC ID" : formData.platform === "meta" ? "BM ID" : "Email"}
                                  </p>
                                  <p className="text-sm font-[400] text-neutral-800 dark:text-white truncate">
                                    {formData.bmIdOrEmail || "—"}
                                  </p>
                                </div>
                                {showPages && (
                                  <div>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Pages</p>
                                    <p className="text-sm font-[400] text-neutral-800 dark:text-white">
                                      {formData.pages.filter((p) => p.trim() !== "").length > 0
                                        ? `${formData.pages.filter((p) => p.trim() !== "").length} pages`
                                        : "—"}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Create Mode – Step 1 */}
                      {!isEditing && currentStep === 1 && (
                        <div className="space-y-6">
                          {isAdmin && (
                            <div>
                              <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                                Select Client
                              </label>
                              <SearchableSelect
                                options={clientOptions}
                                value={selectedClientId}
                                onChange={(v) => setSelectedClientId(v)}
                                placeholder="Choose a client"
                                icon={<Globe size={16} />}
                                error={errors.client}
                              />
                              {errors.client && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertCircle size={12} /> {errors.client}
                                </p>
                              )}
                            </div>
                          )}

                          <div>
                            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                              Select Platform
                            </label>
                            <div className="grid grid-cols-4 gap-3">
                              {["meta", "tiktok", "google", "snapchat"].map((p) => (
                                <button
                                  key={p}
                                  type="button"
                                  className={`p-3 h-24 border rounded-xl flex flex-col items-center justify-center gap-2 transition-all ${formData.platform === p
                                    ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500"
                                    : "border-neutral-200 dark:border-neutral-800 dark:bg-neutral-900/30 hover:border-blue-300 dark:hover:border-blue-700"
                                    }`}
                                  onClick={() => handleChange("platform", p)}
                                >
                                  <div className={`p-2 h-10 w-10 flex justify-center items-center rounded-full ${formData.platform === p
                                    ? "bg-blue-100 dark:bg-blue-800/40 text-blue-600 dark:text-blue-400"
                                    : "bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                                    }`}>
                                    {getPlatformIcon(p)}
                                  </div>
                                  <span className={`text-xs ${formData.platform === p ? "text-blue-700 dark:text-blue-400" : "text-neutral-700 dark:text-neutral-300"
                                    }`}>
                                    {getPlatformDisplayName(p)}
                                  </span>
                                </button>
                              ))}
                            </div>
                            {errors.platform && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} /> {errors.platform}
                              </p>
                            )}
                          </div>

                          {/* USD only */}
                          <div>
                            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                              Account Currency
                            </label>
                            <div className="p-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                                  <span className="text-green-600 dark:text-green-400 text-xs font-bold">$</span>
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-800 dark:text-white">USD</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    All ad accounts use USD currency
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Credit account */}
                          <div>
                            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                              Credit Account
                            </label>
                            {userCreditAccount ? (
                              <div className="p-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                                    <CreditCard size={16} className="text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-800 dark:text-white">
                                      {userCreditAccount.account_name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      USD • {userCreditAccount.user?.fullName || ""}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="p-3 border border-red-200 dark:border-red-800 rounded-xl bg-red-50 dark:bg-red-900/20">
                                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                  <span className="text-sm">
                                    {isAdmin ? "Selected client has no USD credit account." : "No USD credit account found. Please contact support."}
                                  </span>
                                </div>
                              </div>
                            )}
                            {errors.credit_account && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} /> {errors.credit_account}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Create Mode – Step 2 */}
                      {!isEditing && currentStep === 2 && (
                        <div className="space-y-6">
                          <InputCustom
                            label="Account Alias (Optional)"
                            placeholder="Enter account alias"
                            value={formData.accountAlias}
                            onChange={(v) => handleChange("accountAlias", v)}
                            icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
                          />

                          <div>
                            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                              Account Timezone
                            </label>
                            <SearchableSelect
                              options={timezones}
                              value={formData.timezone}
                              onChange={(v) => handleChange("timezone", v)}
                              placeholder="Select timezone"
                              icon={<Clock size={16} />}
                              error={errors.timezone}
                            />
                            {errors.timezone && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} /> {errors.timezone}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                              Category
                            </label>
                            <SearchableSelect
                              options={categories}
                              value={formData.category}
                              onChange={(v) => handleChange("category", v)}
                              placeholder="Select category"
                              icon={<LayoutGrid size={16} />}
                              error={errors.category}
                            />
                            {errors.category && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} /> {errors.category}
                              </p>
                            )}

                            {formData.category === "other" && (
                              <div className="mt-3">
                                <InputCustom
                                  label="Custom Category"
                                  placeholder="Enter your custom category"
                                  value={customCategory}
                                  onChange={(v) => {
                                    setCustomCategory(v)
                                    if (errors.customCategory) {
                                      setErrors((prev) => ({ ...prev, customCategory: "" }))
                                    }
                                  }}
                                  icon={<LayoutGrid size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                                {errors.customCategory && (
                                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                    <AlertCircle size={12} /> {errors.customCategory}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Create Mode – Step 3 */}
                      {!isEditing && currentStep === 3 && (
                        <div className="space-y-6">
                          <InputCustom
                            label="Website URL"
                            placeholder="Enter website URL"
                            value={formData.link}
                            onChange={(v) => handleChange("link", v)}
                            icon={<LinkIcon size={16} className="text-neutral-500 dark:text-neutral-400" />}
                            error={errors.link}
                          />
                          {errors.link && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} /> {errors.link}
                            </p>
                          )}

                          {/* Pages: Meta only, optional */}
                          {showPages && (
                            <div className="w-full">
                              <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                                Page URLs (Optional)
                              </label>

                              {formData.pages.map((page, i) => (
                                <div key={i} className="flex items-center w-full gap-2 mb-2">
                                  <InputCustom
                                    label=""
                                    placeholder="Enter page URL"
                                    className="w-full"
                                    value={page}
                                    onChange={(v) => handlePageChange(i, v)}
                                    icon={<LinkIcon size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePageField(i)}
                                    className="p-2.5 mt-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ))}

                              <button
                                type="button"
                                onClick={addPageField}
                                className="mt-2 text-xs flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                + Add another page
                              </button>

                              {errors.pages && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertCircle size={12} /> {errors.pages}
                                </p>
                              )}
                            </div>
                          )}

                          {/* ID field: platform-specific */}
                          <div>
                            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                              {idLabel}
                            </label>

                            {/* No toggles anymore; single field based on platform */}
                            <InputCustom
                              label=""
                              placeholder={
                                formData.platform === "google" || formData.platform === "snapchat"
                                  ? "Enter email address"
                                  : formData.platform === "tiktok"
                                    ? "Enter BC ID"
                                    : "Enter BM ID"
                              }
                              value={formData.bmIdOrEmail}
                              onChange={(v) => handleChange("bmIdOrEmail", v)}
                              icon={idIcon}
                              error={errors.bmIdOrEmail}
                            />
                            {errors.bmIdOrEmail && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} /> {errors.bmIdOrEmail}
                              </p>
                            )}
                          </div>

                          <div className="p-4 rounded-xl bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 border border-neutral-200 dark:bg-neutral-800">
                            <h3 className="text-xs font-medium text-neutral-800 dark:text-neutral-200 mb-3">Account Summary</h3>
                            <div className="space-y-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-neutral-600 dark:text-neutral-400">Platform:</span>
                                <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                                  {getPlatformDisplayName(formData.platform)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-neutral-600 dark:text-neutral-400">Currency:</span>
                                <span className="text-neutral-900 dark:text-neutral-200 font-medium">USD</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-neutral-600 dark:text-neutral-400">Credit Account:</span>
                                <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                                  {userCreditAccount?.account_name || "Not available"}
                                </span>
                              </div>
                              {formData.timezone && (
                                <div className="flex justify-between">
                                  <span className="text-neutral-600 dark:text-neutral-400">Timezone:</span>
                                  <span className="text-neutral-900 dark:text-neutral-200 font-medium">{formData.timezone}</span>
                                </div>
                              )}
                              {showPages && formData.pages.filter((p) => p.trim() !== "").length > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-neutral-600 dark:text-neutral-400">Page URLs:</span>
                                  <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                                    {formData.pages.filter((p) => p.trim() !== "").length} pages
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </form>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-6 flex justify-between">
                  {isEditing || currentStep > 1 ? (
                    <button
                      type="button"
                      onClick={isEditing ? onClose : () => setCurrentStep((s) => Math.max(1, s - 1))}
                      className="px-5 h-10 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors text-xs flex items-center gap-2"
                      disabled={processing}
                    >
                      {isEditing ? "Cancel" : (<><ChevronDown className="rotate-90" size={14} /> Back</>)}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 h-10 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors text-xs flex items-center gap-2"
                      disabled={processing}
                    >
                      <X size={14} /> Cancel
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-5 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-xs flex items-center gap-2"
                    disabled={
                      processing ||
                      (!isEditing && (
                        (!userCreditAccount) || (isAdmin && !selectedClientId)
                      ))
                    }
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : !isEditing && currentStep < totalSteps ? (
                      <>
                        Continue
                        <ChevronDown className="-rotate-90" size={14} />
                      </>
                    ) : (
                      <>
                        {isEditing ? "Update Account" : "Create Account"}
                        <Check size={14} />
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default AdAccountFormModal
